import { createFileRoute, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { domAnimation, LazyMotion } from 'motion/react';
import * as z from 'zod';

import { OAuthProvider } from '#modules/user/features/auth/constants/providers';
import { useEnsureTimeZoneOffset } from '#modules/user/features/profile/hooks/useEnsureTimeZoneOffset';
import { useLastVisits } from '#modules/user/features/profile/hooks/useLastVisits';
import { usePageView } from '#features/analytics/hooks/usePageView';
import { CallOverlay } from '#features/calls/components/CallOverlay';
import { ErrorNotFound } from '#features/error/components/ErrorNotFound';
import { MainLayout } from '#features/layout/components/MainLayout';
import { useIdleReporter } from '#features/presence/hooks/useIdleReporter';
import { usePresenceSync } from '#features/presence/hooks/usePresence';
import { useRealtimeNotifications } from '#features/realtime/hooks/useRealtimeNotifications';
import { useRealtimeSubscription } from '#features/realtime/hooks/useRealtimeSubscription';
import { SESSION_BROADCAST_TOPICS } from '#features/realtime/topics';
import { VoiceWidgetHost } from '#features/voice/components/VoiceWidget/VoiceWidgetHost';

// Make sure to return the specific export containing the feature bundle.
// const loadFeatures = () =>
//   import('#features/ui/constants/motionFeatures').then((res) => res.domAnimation);

// Схема висит на layout'е, под которым лежит весь сайт, а оба параметра приходят снаружи —
// из OAuth-возвратов и чужих ссылок. Разбираем их мягко: без `.catch` неизвестный провайдер
// (`?provider=xbox`) или задвоенный параметр (`?url=a&url=b`) ронял SSR всей страницы в 500
// вместо того, чтобы просто вычиститься из адреса.
const layoutSchema = z.object({
  provider: z.enum(OAuthProvider).optional().catch(undefined),
  url: z.string().optional().catch(undefined),
});

const Providers = ({ children }: { children: React.ReactNode }) => {
  return (
    <LazyMotion features={domAnimation}>
      <MainLayout>{children}</MainLayout>
    </LazyMotion>
  );
};

export const Route = createFileRoute('/{-$locale}/_layout')({
  component: LayoutComponent,
  notFoundComponent: () => (
    <Providers>
      <ErrorNotFound />
    </Providers>
  ),
  validateSearch: zodValidator(layoutSchema),
});

function LayoutComponent() {
  usePageView();
  useLastVisits();
  useEnsureTimeZoneOffset();
  useRealtimeNotifications();
  // Один опрос присутствия на всё приложение: экраны только регистрируют, на кого
  // смотрят, а спрашивает сервер отсюда — иначе список чатов, открытый чат и лента
  // тянули бы статус каждый сам и показывали снимки разного возраста.
  usePresenceSync();
  // Сообщаем серверу, за экраном человек или отошёл: по самому соединению этого не видно,
  // а от этого зависят и жёлтый статус, и то, придёт ли ему push о сообщении.
  useIdleReporter();
  // Subscribe to broadcast session/post topics once for the whole app, so every
  // page receives live updates (toasts + cache invalidation). Kept here at the
  // always-mounted layout root rather than per-page, so navigating between pages
  // doesn't unsubscribe topics out from under other pages.
  useRealtimeSubscription(SESSION_BROADCAST_TOPICS);

  return (
    <Providers>
      <Outlet />
      {/* Global 1:1 call UI (incoming ring / in-call window) — lives next to the WS
          hook so call events always have a mounted consumer. */}
      <CallOverlay />
      {/* Голос сессии: соединение живёт здесь, выше роутера, поэтому переход между страницами
          разговор не прерывает. */}
      <VoiceWidgetHost />
    </Providers>
  );
}
